import { apiFetch, type Document } from '@/api/v2Client';

/**
 * Wave E.1 — Compiler API client.
 *
 * Kept separate from the main `v2Client` namespace so the Compiler
 * surface can grow independently in Wave E.2-E.6 (right-pane tabs,
 * compare, validation framework, PDF/A, eCTD validator). Reuses
 * `apiFetch` for envelope-unwrapping + auth-token plumbing.
 */

export interface SectionNode {
  readonly code: string;
  readonly label: string;
  readonly moduleNumber: 1 | 2 | 3 | 4 | 5;
  readonly children: readonly SectionNode[];
  /** TODO-060 — number of non-deleted Documents classified to this section. */
  readonly documentCount: number;
}

export type CompilerRegion = 'US' | 'EU' | 'CA' | 'AU' | 'JP' | 'CH' | 'all';

export interface SectionTreeResponse {
  readonly dtdSource: 'fallback' | 'dtd';
  readonly dtdVersion: string;
  readonly region: CompilerRegion;
  readonly tree: readonly SectionNode[];
}

export const compilerApi = {
  /**
   * Fetch the canonical eCTD section tree for the submission's region.
   * Backed by the Wave E.6 DTD parser when available, otherwise a
   * hardcoded fallback tree.
   */
  sectionTree(submissionId: string): Promise<SectionTreeResponse> {
    return apiFetch<SectionTreeResponse>(
      `/api/v1/submissions/${encodeURIComponent(submissionId)}/section-tree`,
    );
  },

  /**
   * Fetch documents filtered by submission + (optionally) eCTD section code.
   * Wave E.1 introduces the `sectionCode` query param on the existing
   * `/api/v1/documents` endpoint.
   */
  documentsBySection(
    submissionId: string,
    sectionCode: string | null,
  ): Promise<readonly Document[]> {
    const params = new URLSearchParams();
    params.set('submissionId', submissionId);
    if (sectionCode && sectionCode.trim() !== '') {
      params.set('sectionCode', sectionCode.trim());
    }
    return apiFetch<readonly Document[]>(`/api/v1/documents?${params.toString()}`);
  },

  /**
   * phase-links-submission-view — the submission-wide links inventory
   * backing the centre Links tab. One row per link annotation across every
   * document of the sequence, evaluated by the same WAVE-E4 evaluator the
   * Inspector's per-document tab reports from.
   */
  submissionLinks(submissionId: string): Promise<SubmissionLinksResponse> {
    return apiFetch<SubmissionLinksResponse>(
      `/api/v1/submissions/${encodeURIComponent(submissionId)}/links`,
    );
  },
};

// ----- phase-links-submission-view — submission-wide links types -----

export type SubmissionLinkKind =
  | 'page-anchor'
  | 'cross-document'
  | 'external'
  | 'remote'
  | 'unknown';
export type SubmissionLinkStatus =
  | 'resolves'
  | 'broken'
  | 'external'
  | 'remote-unverified'
  | 'incomplete';

export interface SubmissionLinkRow {
  readonly sourceDocumentId: string;
  readonly sourceFileName: string;
  readonly page: number;
  readonly kind: SubmissionLinkKind;
  readonly target: string;
  readonly targetDocumentId: string | null;
  readonly status: SubmissionLinkStatus;
  readonly reason: string | null;
  /** phase-region-format-rules-and-docx-links — extraction path. */
  readonly method: 'pdf' | 'docx';
  /** 'advisory' = labelled pre-conversion Word-side check; never the truth. */
  readonly check: 'authoritative' | 'advisory';
}

export interface UnanalysedDocument {
  readonly documentId: string;
  readonly fileName: string;
  readonly reason: string;
}

export interface SubmissionLinksResponse {
  readonly submissionId: string;
  readonly documentCount: number;
  readonly links: readonly SubmissionLinkRow[];
  readonly unanalysed: readonly UnanalysedDocument[];
  readonly counts: {
    readonly total: number;
    readonly resolves: number;
    readonly broken: number;
    readonly external: number;
    /** Other-file (GoToR/Launch) targets — not verified without package context. */
    readonly remoteUnverified: number;
    /** Resolutions that hit a cycle/budget — reported, never asserted valid. */
    readonly incomplete: number;
    readonly advisory: number;
    readonly unanalysedDocuments: number;
  };
  /** B.8 caveat — present whenever any Word-native result is in `links`. */
  readonly docxCheckCaveat: string | null;
}
